import React from "react";
import { Input } from "@progress/kendo-react-inputs";
import { DatePicker, DateRangePicker } from "@progress/kendo-react-dateinputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { BOOLEAN_OPTIONS, disableFields } from "./filterUtils";
import CustomDropdown from "src/components/customDropdown";
import { Label } from "@progress/kendo-react-labels";

export const DateComponent = React.memo(({ item, idx, handleChange }) => {
  const operator = item?.operator?.operator_label;
  const isBetween = operator === "Between" || operator === "Not Between";
  const fieldName = item?.value?.field;
  const disabled = disableFields(operator);
  // Ensure inputValue is always in the correct format
  const externalValue = item?.inputValue?.value;
  const initialValue = React.useMemo(() => {
    if (isBetween) {
      if (externalValue && typeof externalValue === "object") {
        return {
          start: externalValue.start ? new Date(externalValue.start) : null,
          end: externalValue.end ? new Date(externalValue.end) : null,
        };
      }
      return { start: null, end: null };
    }

    return externalValue ?? null;
  }, [externalValue, isBetween]);

  const [dateValue, setDateValue] = React.useState(initialValue);

  React.useEffect(() => {
    setDateValue(initialValue);
  }, [initialValue]);

  const handleDateChange = (val, position = null) => {
    if (isBetween) {
      const updated = { start: val?.start, end: val?.end };
      setDateValue(updated);
      handleChange({ ...item, inputValue: { field: fieldName, value: updated } }, idx);
    } else {
      setDateValue(val);
      handleChange({ ...item, inputValue: { field: fieldName, value: val } }, idx);
    }
  };

  const commonProps = {
    style: { width: "250px" },
    disabled,
  };

  const startDateInputSettings = {
    spinners: false,
    label: "Start",
    format: "yyyy-MM-dd",
    placeholder: "yyyy-mm-dd",
  };

  const endDateInputSettings = {
    spinners: false,
    label: "End",
    format: "yyyy-MM-dd",
    placeholder: "yyyy-mm-dd",
  };

  return (
    <div className={isBetween ? "input-container" : "input-container martop20"}>
      {isBetween ? (
        <>
          <DateRangePicker
            value={dateValue}
            onChange={(e) => {
              handleDateChange(e.value);
            }}
            startDateInputSettings={startDateInputSettings}
            endDateInputSettings={endDateInputSettings}
          />
          {item?.errorInfo?.error && <Label style={{ fontSize: 11, color: "red" }}>{item.errorInfo.errorMsg}</Label>}
        </>
      ) : (
        <>
          <DatePicker
            name={fieldName}
            value={
              Array.isArray(dateValue)
                ? null
                : dateValue === "0000-00-00 00:00:00"
                  ? null
                  : dateValue
                    ? new Date(dateValue) || null
                    : null
            }
            format="yyyy-MM-dd"
            formatPlaceholder={{
              year: "yyyy",
              month: "mm",
              day: "dd",
            }}
            placeholder="yyyy-mm-dd"
            onChange={(e) => handleDateChange(e.value)}
            {...commonProps}
          />
          {item?.errorInfo?.error && <Label style={{ fontSize: 11, color: "red" }}>{item.errorInfo.errorMsg}</Label>}
        </>
      )}
    </div>
  );
});

export const InputComponent = React.memo(({ item, idx, type, handleChange }) => {
  const [value, setValue] = React.useState(item?.inputValue?.value ?? "");

  React.useEffect(() => {
    setValue(typeof item?.inputValue?.value === "number" ? item.inputValue.value : (item?.inputValue?.value ?? ""));
  }, [item]);

  const isInOperator = item?.operator?.value === "IN" || item?.operator?.value === "NOT IN";

  return (
    <>
      <div className="input-container martop20">
        <Input
          type={type}
          value={value}
          name={item?.value?.field}
          onChange={(e) => setValue(e.value)}
          placeholder={isInOperator ? (item?.value?.type === "number" ? "(1,2)" : "('abc','abc')") : ""}
          onBlur={() => handleChange({ ...item, inputValue: { field: item?.value?.field, value } }, idx)}
          style={{ width: "200px" }}
          disabled={disableFields(item?.operator?.operator_label)}
        />
        {item?.errorInfo?.error && <Label style={{ fontSize: 11, color: "red" }}>{item.errorInfo.errorMsg}</Label>}
      </div>
    </>
  );
});

export const BooleanDropdown = React.memo(({ item, idx, handleChange }) => {
  const getValue = () => {
    if (typeof item?.inputValue?.value === "string") {
      let find = BOOLEAN_OPTIONS.find((opt) => opt.id.toString() === item.inputValue.value);
      return find ? find : null;
    }
    return item?.inputValue?.value ?? null;
  };
  return (
    <div className="input-container martop20">
      <DropDownList
        name={item?.value?.field}
        data={BOOLEAN_OPTIONS}
        value={getValue()}
        onChange={(e) => {
          handleChange({ ...item, inputValue: { field: e.target.name, value: e.value } }, idx);
        }}
        textField="text"
        dataItemKey="id"
        style={{ width: "150px" }}
        disabled={disableFields(item?.operator?.operator_label)}
      />
    </div>
  );
});

export const DropdownComponent = React.memo(
  ({ item, idx, handleChange, config, originalData, actions, eventTypeFields, cascadeLoadingSet, lockedFieldsRef }) => {
    const isCascadeLoading = cascadeLoadingSet?.has(item?.value?.field) ?? false;
    const [filteredData, setFilteredData] = React.useState(config?.data);

    React.useEffect(() => {
      setFilteredData(config?.data);
    }, [config.data]);

    // Stable per-instance guard: survives fieldConfigMap useMemo recreations.
    const didFetchRef = React.useRef(false);

    React.useEffect(() => {
      const field = item?.value?.field;
      if (!field) return;
      if (lockedFieldsRef?.current?.[field]) {
        didFetchRef.current = true;
        return;
      }
      if (config?._calledOnce?.[field]) {
        didFetchRef.current = true;
        return;
      }
      if (didFetchRef.current) return;
      if (typeof actions?.[field] === "function" && !config?.data?.length && !config?.loading) {
        didFetchRef.current = true;
        Promise.resolve(actions[field](eventTypeFields)).catch(() => {});
      }
    }, [item?.value?.field]);

    React.useEffect(() => {
      if (isCascadeLoading) return;
      const isNullOperator = disableFields(item?.operator?.operator_label);
      if (config.data && config.data.length > 0 && !item?.inputValue?.value && !isNullOperator) {
        handleChange(
          {
            ...item,
            inputValue: {
              field: item?.value?.field,
              value: config.data[0],
            },
          },
          idx
        );
      }
    }, [config.data, isCascadeLoading]);

    const handleDropdownChange = React.useCallback(
      (e) => {
        const field = item?.value?.field;
        handleChange(
          {
            ...item,
            inputValue: {
              field,
              value: e,
            },
          },
          idx
        );
        // if (actions[field] && (!config?.data || config?.data.length === 0)) {
        //   actions[field](eventTypeFields);
        // }
      },
      [handleChange, item, idx]
    );

    const handleOpen = React.useCallback(() => {
      config.setData((prev) => {
        if (prev[config.stateKey] !== config.originalData) {
          return {
            ...prev,
            [config.stateKey]: config.originalData,
          };
        }
        return prev;
      });
      setFilteredData(config.data);
    }, [config]);

    const handleFilterChange = React.useCallback(
      (e) => {
        setFilteredData(e);
      },
      [config.data]
    );

    const renderDropValue = () => {
      const value = item?.inputValue?.value;

      // If we have hydrated text from backend, use it directly
      if (item?.inputValue?.text) {
        // Extract ID from value
        let id = null;
        if (Array.isArray(value) && value.length > 0) {
          const first = value[0];
          id = first != null && typeof first === "object" ? first.id : first;
        } else if (value != null && typeof value === "object") {
          id = value.id;
        } else if (typeof value === "number" || typeof value === "string") {
          id = value;
        }

        // Return simple object matching dropdown's expected structure
        return {
          id: String(id ?? ""), // Ensure it's a string to match dropdown data
          text: item.inputValue.text,
          dataItemKey: item.inputValue.dataItemKey || item.value?.field,
        };
      }

      if (!Array.isArray(config?.data) || config.data.length === 0) {
        return Array.isArray(value) || typeof value === "object" ? value || {} : {};
      }
      let id = null;
      if (Array.isArray(value) && value.length > 0) {
        id = value[0]?.toString();
      } else if (typeof value === "number" || typeof value === "string") {
        id = value.toString();
      }

      // Try to find match with string comparison first, then try number comparison
      if (id) {
        const match =
          config.data.find((d) => d.id?.toString() === id) ||
          config.data.find((d) => d[config?.dataItemKey]?.toString() === id);
        return match || {};
      }
      return value || {};
    };

    return (
      <>
        <div className="input-container martop20">
          <CustomDropdown
            style={{ width: "200px" }}
            name={item?.value?.field}
            data={filteredData}
            textField="text"
            dataItemKey="id"
            onOpen={handleOpen}
            valueData={renderDropValue()}
            onChange={handleDropdownChange}
            filterable={true}
            onFilterChange={handleFilterChange}
            autoClose={false}
            autoBind={true}
            isSelectALl={true}
            dataList={originalData}
            loading={config.loading || isCascadeLoading}
            disabled={config.loading || isCascadeLoading || disableFields(item?.operator?.operator_label)}
          />
          {item?.errorInfo?.error && <Label style={{ fontSize: 11, color: "red" }}>{item.errorInfo.errorMsg}</Label>}
        </div>
      </>
    );
  }
);
